// ============================================================
// 티켓 투 라이드 클라이언트 store (카탄 store/catan.ts 패턴 일관)
//   SoT: H:\AI\AI_Setting\보드겜돌이\티켓투라이드.md §3·§4·§5
//   SEQ=10146 — UX 본문 결선 (서브 상태 / BFS 라이브 갱신 / 본인 핸드 차감 /
//               applyGameState 결과셋 10종 매핑 / applyTicketsKept 본인 이동).
// ============================================================
import {
  buildPlayerAdjacency,
  isConnected as ttrIsConnected,
  ClaimedEdgeUx,
} from '../util/ttrGraph'

// ── 게임 / 플레이어 ───────────────────────────────────────
export interface TtrGame {
  matchId:                     number
  roomCode:                    string
  seed:                        number
  status:                      string   // 'DEAL_TICKETS' | 'MAIN_TURN' | 'FINAL_TURN' | 'GAME_END'
  currentPhase:                string | null
  currentTurnUserCode:         number | null
  turnNo:                      number
  finalTurnTriggerUserCode?:   number | null
  winnerUserCode?:             number | null
  maxSeats:                    number
}

export interface TtrPlayer {
  matchId:         number
  userCode:        number
  nickname:        string
  seatIndex:       number
  /** 시작 순서 셔플 결과 (SoT §2-4.1, sp_TtrEndTurn ORDER BY) */
  turnOrder:       number
  color:           string   // RED|BLUE|GREEN|YELLOW|BLACK
  score:           number
  trainRemain:     number
  isLongestPath:   boolean
  finalScore?:     number | null
}

// ── 정적 마스터 (보드 렌더용, 게임 무관 — 1회 로드 후 재사용) ─
export interface TtrCity {
  cityId:    number
  name:      string
  displayX:  number   // 비율 0~1 (Konva Stage width 곱해 환산)
  displayY:  number
  /** TTR_CITY.IMPORTANCE (1=Hub / 2=Major / 3=Minor) — SEQ=10186 ③ §3-A-detail ⑧.
   *  운영 DDL 미적용 환경에서는 undefined → 클라가 connections 기반 fallback 적용. */
  importance?: number
}

export interface TtrRoute {
  routeId:   number
  cityAId:   number
  cityBId:   number
  color:     string   // GRAY|RED|...|BLACK
  length:    number
  laneNo:    number
  isDouble:  boolean
}

export interface TtrTicket {
  ticketId:  number
  cityAId:   number
  cityBId:   number
  points:    number
  isLong:    boolean
}

// ── 보드 동적 상태 ────────────────────────────────────────
/** 노선 점유 (per claim). routeId+laneNo composite key. */
export interface TtrRouteClaim {
  routeId:        number
  laneNo:         number
  ownerUserCode:  number
  color:          string   // 점유자 좌석 색 (렌더용)
  turnNo:         number
}

/** Face-up 5장 슬롯. cardColor='WILD' 시 와일드. */
export interface TtrFaceUpSlot {
  slotIndex:   number   // 0~4
  cardColor:   string   // 8색 + WILD
}

// ── 서브 상태 (SoT §2-6 sub-state) ───────────────────────
/**
 * 본인 턴 동안의 미니 상태 머신.
 *   IDLE                  — 액션 선택 대기 (3액션 중 1)
 *   AWAIT_SECOND_CARD     — 첫 카드 1장 뽑은 후 두 번째 슬롯/덱 선택 대기
 *                           (와일드 슬롯을 첫 1장으로 뽑으면 즉시 종료 — 본 상태 진입 X)
 *   AWAIT_TICKET_KEEP     — 티켓 3장 노출 후 keep 선택 대기 (MAIN 1~3 / SETUP 2~3)
 * 서버는 권위, 클라는 UX 가이드 (apply* 가 전이 / 본인 클라 한정).
 */
export type TtrSubPhase = 'IDLE' | 'AWAIT_SECOND_CARD' | 'AWAIT_TICKET_KEEP'

// ── 본인 핸드 (정보 비대칭, 본인 전용) ─────────────────────
/** 본인 핸드 색별 카운트 (vw_TtrPlayerHandSummary 응답 그대로). */
export type TtrMyHandSummary = Record<string, number>   // color → count

/** 본인 보유 티켓 (HAND, 종료 전까지 본인만). */
export interface TtrMyTicket {
  ticketId:     number
  cityAId:      number
  cityBId:      number
  points:       number
  isLong:       boolean
  /** 서버 직렬화 'HAND' | 'PENDING_KEEP' (sp_TtrGameLoad 6번째 결과셋 ZONE 컬럼). */
  zone?:        string
  /** 클라 BFS 진행 중 표시 (서버 권위 계산은 종료 시). */
  isCompleteUx?: boolean
}

/** PENDING_KEEP (SETUP / MAIN 티켓 드로우 직후 keep 결정 대기). */
export interface TtrPendingTicket {
  ticketId: number
  cityAId:  number
  cityBId:  number
  points:   number
  isLong:   boolean
}

// ── 타인 정보 (정보 비대칭: 카운트만) ──────────────────────
export interface TtrOtherCounts {
  cardCount:    number   // 핸드 카드 총수
  ticketCount:  number   // 보유 티켓 수
}

// ── 페이로드 타입 (broadcast 12종, SoT §4-2) ──────────────
export interface TtrGameStartedPayload {
  matchId:           number
  roomCode:          string
  players:           TtrPlayer[]
  seed:              number
  faceUpCards:       TtrFaceUpSlot[]
  deckRemain:        number
  ticketDeckRemain:  number
}

export interface TtrTicketsDealtPayload {
  userCode:     number
  ticketCount:  number   // 전체엔 카운트만
}

/** Clients.Caller 전용 — 본인 PENDING_KEEP 티켓 실제 데이터. */
export interface TtrTicketsDealtCallerPayload {
  tickets: TtrPendingTicket[]
}

export interface TtrTurnStartedPayload {
  matchId:              number
  currentTurnUserCode:  number
  turnNo:               number
  currentPhase:         string
}

export interface TtrCardDrawnPayload {
  userCode:           number
  source:             'FACE_UP' | 'DECK'
  slotIndexIfFaceUp?: number | null
  newFaceUpCards:     TtrFaceUpSlot[]
  deckRemain:         number
  discardRemain:      number
}

/** Clients.Caller 전용 — 본인이 뽑은 카드 실제 색.
 *  catch #14 잔존 회수 (SEQ=10201 STEP=4, 2026-05-26): awaitSecond 권위 추가.
 *    서버 SP `sp_TtrDrawTrainCard.out_auto_end_turn` 역수. 1=AWAIT_SECOND_CARD 진입 / 0=turn end·2번째 완료.
 *    기존 클라가 cardColor='WILD'면 무조건 IDLE 유지 추측 → 비공개 덱 첫 와일드 룰(§2-2) 위반.
 *    본 권위 필드로 공개/비공개 분기 불요. */
export interface TtrCardDrawnCallerPayload {
  cardColor:   string
  awaitSecond: number  // 0 또는 1 (서버 권위 — SP out_auto_end_turn 역수)
}

export interface TtrRouteClaimedPayload {
  routeId:         number
  laneNo:          number
  userCode:        number
  colorUsed:       string
  wildCount:       number
  trainsConsumed:  number
  scoreDelta:      number
  newScore:        number
  // catch #21 fix (SEQ=10205 STEP=1): 사용 카드 → DISCARD 이동 후 신규 DISCARD 카운트
  newDiscardRemain?: number
}

export interface TtrScoreUpdatedPayload {
  userCode: number
  newScore: number
}

export interface TtrTicketsDrawnPayload {
  userCode:    number
  ticketCount: number
}

export interface TtrTicketsDrawnCallerPayload {
  tickets: TtrPendingTicket[]
}

export interface TtrTicketsKeptPayload {
  userCode:  number
  keepCount: number
}

export interface TtrTrainRemainUpdatedPayload {
  userCode:    number
  trainRemain: number
}

export interface TtrFaceUpRefilledPayload {
  newFaceUpCards: TtrFaceUpSlot[]
  deckRemain:     number
}

export interface TtrFinalTurnTriggeredPayload {
  triggerUserCode:      number
  currentTurnUserCode:  number
}

export interface TtrScoreBreakdown {
  userCode:      number
  routeScore:    number
  ticketScore:   number
  longestBonus:  number
  total:         number
}

export interface TtrGameEndedPayload {
  matchId:             number
  winnerUserCode:      number | null   // 동률 시 null (scoreBreakdown 으로 식별)
  scoreBreakdown:      TtrScoreBreakdown[]
  longestPathHolders:  number[]        // user_code (동률 다수 가능)
  allTickets:          { userCode: number; tickets: TtrMyTicket[] }[]  // 종료 시 전원 공개
}

export interface TtrActionErrorPayload {
  userCode:     number
  errorCode:    string
  errorMessage: string
}

// ════════════════════════════════════════════════════════
// 내부 상태 (모듈 단위, 카탄 store 패턴)
// ════════════════════════════════════════════════════════

let _game:             TtrGame | null = null
let _players:          TtrPlayer[]    = []

// 정적 마스터 (게임 무관)
let _cities:           TtrCity[]      = []
let _routes:           TtrRoute[]     = []
let _ticketsMaster:    TtrTicket[]    = []

// 동적 보드 상태
let _claims:           TtrRouteClaim[] = []
let _faceUp:           TtrFaceUpSlot[] = []
let _deckRemain:       number          = 0
let _discardRemain:    number          = 0
let _ticketDeckRemain: number          = 0

// 본인 전용 (정보 비대칭)
let _myHandSummary:    TtrMyHandSummary = {}
let _myTickets:        TtrMyTicket[]    = []
let _myPendingTickets: TtrPendingTicket[] = []   // AWAIT_TICKET_KEEP 동안만
let _myPendingFirstCard: { color: string; sourceSlot: number | null } | null = null  // AWAIT_SECOND_CARD

// 본인 턴 서브 상태 (UX 가이드)
let _mySubPhase:       TtrSubPhase = 'IDLE'
// 본인 user_code 캐시 (BFS 라이브 갱신·서브 상태 분기 — applyGameStarted 시 보정 필요할 수 있음)
let _myUserCode:       number | null = null
// SEQ=10199 STEP=1: 한 턴 1액션 가드 — 본인 턴 동안 액션(카드 드로우/노선 클레임/티켓 드로우) 완료 여부.
//   applyCardDrawn(Caller) / applyRouteClaimed / applyTicketsDrawn(Caller) → true
//   applyTurnStarted / clearGame → false
//   UI 가드: 액션 1회 후 추가 액션 버튼 비활성화 + 턴 종료 버튼은 true일 때만 활성화.
let _actionDoneThisTurn: boolean = false

// SEQ=10200 STEP=1: catch #11 노선 클레임 시각 강화 — 최근 점유 lane 1.5초 추적.
//   applyRouteClaimed → SET (이전 _recentClaim 타이머 clearTimeout 후 신규 ts)
//   1500ms 후 자동 CLEAR + _notify(). 클라 보드 ring overlay + `+N` popover 트리거.
//   본인/타인 클레임 모두 강조 (사용자 시인성 = 본인 + 타인 모두 필요).
export interface TtrRecentClaim {
  routeId:       number
  laneNo:        number
  scoreDelta:    number
  ownerUserCode: number
  ownerColor:    string  // 좌석 색 ENUM (PLAYER_COLOR_HEX 매핑용)
  ts:            number  // Date.now() epoch — Konva Tween 진행도 계산용
}
let _recentClaim:       TtrRecentClaim | null = null
let _recentClaimTimer:  ReturnType<typeof setTimeout> | null = null

// 타인 (카운트만)
let _otherCounts:      Record<number, TtrOtherCounts> = {}

// 종료 직후 결과
let _gameEnded:        TtrGameEndedPayload | null = null

// ── 구독 ─────────────────────────────────────────────────
type Listener = () => void
const _listeners: Set<Listener> = new Set()
function _notify() { _listeners.forEach(fn => fn()) }

export function subscribeTicket(fn: Listener): () => void {
  _listeners.add(fn)
  return () => { _listeners.delete(fn) }
}

// ════════════════════════════════════════════════════════
// 조회 (ticketStore.getXxx)
// ════════════════════════════════════════════════════════
export const ticketStore = {
  getGame():              TtrGame | null               { return _game },
  getPlayers():           TtrPlayer[]                  { return _players },
  getCities():            TtrCity[]                    { return _cities },
  getRoutes():            TtrRoute[]                   { return _routes },
  getTicketsMaster():     TtrTicket[]                  { return _ticketsMaster },
  getClaims():            TtrRouteClaim[]              { return _claims },
  getFaceUp():            TtrFaceUpSlot[]              { return _faceUp },
  getDeckRemain():        number                       { return _deckRemain },
  getDiscardRemain():     number                       { return _discardRemain },
  getTicketDeckRemain():  number                       { return _ticketDeckRemain },
  getMyHandSummary():     TtrMyHandSummary             { return _myHandSummary },
  getMyTickets():         TtrMyTicket[]                { return _myTickets },
  getMyPendingTickets():  TtrPendingTicket[]           { return _myPendingTickets },
  getMyPendingFirstCard(): { color: string; sourceSlot: number | null } | null { return _myPendingFirstCard },
  getOtherCounts():       Record<number, TtrOtherCounts> { return _otherCounts },
  getGameEnded():         TtrGameEndedPayload | null   { return _gameEnded },
  getMySubPhase():        TtrSubPhase                  { return _mySubPhase },
  getMyUserCode():        number | null                { return _myUserCode },
  /** SEQ=10199 STEP=1: 한 턴 1액션 가드 — 본인 현 턴 액션 수행 여부. */
  getActionDoneThisTurn(): boolean                     { return _actionDoneThisTurn },
  /** SEQ=10200 STEP=1: catch #11 — 최근 점유 lane (1.5초 유효). 없으면 null. */
  getRecentClaim():       TtrRecentClaim | null        { return _recentClaim },

  /** 본인 user_code 명시 등록 (PlayTicket 마운트 시 authState.userCode 주입). */
  setMyUserCode(uc: number): void { _myUserCode = uc },

  /**
   * 본인 점유 노선 그래프로 보유 티켓 일괄 isCompleteUx 라이브 갱신.
   * 호출 트리거: applyRouteClaimed(본인) / applyGameState 결과셋.
   * 권위 점수 계산은 서버 (TtrGameEnded.scoreBreakdown). 본 함수는 본인 시인성만.
   */
  recalcMyTicketCompletion(): void {
    if (_myUserCode === null) return
    const myEdges: ClaimedEdgeUx[] = _claims
      .filter(c => c.ownerUserCode === _myUserCode)
      .map(c => {
        const route = _routes.find(r => r.routeId === c.routeId)
        return {
          routeId:       c.routeId,
          laneNo:        c.laneNo,
          cityAId:       route?.cityAId ?? 0,
          cityBId:       route?.cityBId ?? 0,
          length:        route?.length ?? 0,
          ownerUserCode: c.ownerUserCode,
        }
      })
      .filter(e => e.cityAId !== 0 && e.cityBId !== 0)
    const adj = buildPlayerAdjacency(myEdges)
    _myTickets = _myTickets.map(t => ({
      ...t,
      isCompleteUx: ttrIsConnected(adj, t.cityAId, t.cityBId),
    }))
  },

  // ════════════════════════════════════════════════════════
  // 갱신 / 적용 (apply*) — Phase 1+ 결선
  // ════════════════════════════════════════════════════════

  /** 정적 마스터 로드 (REST 1회 호출 후 캐시). */
  applyStaticMasters(cities: TtrCity[], routes: TtrRoute[], tickets: TtrTicket[]): void {
    _cities = cities
    _routes = routes
    _ticketsMaster = tickets
    _notify()
  },

  /**
   * GameState (재연결 / 진입) 결과로 전체 상태 복원.
   *
   * sp_TtrGameLoad 10 결과셋(SEQ=10145 smoke 확정) 매핑:
   *   ① matchHeader  → _game
   *   ② players       → _players
   *   ③ faceUpCards   → _faceUp (5슬롯)
   *   ④ routes        → _routes (정적 마스터, 캐시되어 있을 수 있음)
   *   ⑤ myHand        → _myHandSummary (본인 색별 카운트)
   *   ⑥ myTickets     → _myTickets (HAND/PENDING_KEEP, isCompleteUx 라이브)
   *   ⑦ cardCounts    → _otherCounts.cardCount
   *   ⑧ ticketCounts  → _otherCounts.ticketCount
   *   ⑨ trainRemain   → _players[i].trainRemain (전체)
   *   ⑩ ticketDeckRemain → _ticketDeckRemain
   * routeClaims는 별도 결과셋(GameLoad 결과셋 보강 trigger) 또는 broadcast 누적 복원.
   *
   * 카탄 SEQ=10077 GameState 패턴 — JSON 직렬화는 서버 책임.
   */
  applyGameState(payload: Record<string, unknown>): void {
    const p = payload as {
      // SEQ=10194 STEP=3: DAL.ReadAllAsList는 단행 결과셋도 배열로 직렬화.
      // matchHeader는 배열([{...}]) 또는 단일 객체({...}) 양쪽 허용.
      matchHeader?:        Partial<TtrGame> | Array<Partial<TtrGame>>
      players?:            TtrPlayer[]
      faceUpCards?:        TtrFaceUpSlot[]
      routes?:             TtrRouteClaim[]   // SEQ=10187: 서버 직렬화 키 = routes, 실체는 TTR_ROUTE_CLAIM (점유 노선)
      routeClaims?:        TtrRouteClaim[]
      // myHand: DAL 배열([{color,count},...]) 또는 기존 Record<string,number> 양쪽 허용.
      myHand?:             TtrMyHandSummary | Array<{ color: string; count: number }>
      myTickets?:          TtrMyTicket[]
      myPendingTickets?:   TtrPendingTicket[]
      cardCounts?:         Array<{ userCode: number; cardCount: number }>
      ticketCounts?:       Array<{ userCode: number; ticketCount: number }>
      trainRemain?:        Array<{ userCode: number; trainRemain: number }>
      // ticketDeckRemain: DAL 배열([{ticketDeckRemain,ticketDiscarded}]) 또는 number 양쪽 허용.
      ticketDeckRemain?:   number | Array<{ ticketDeckRemain?: number }>
      deckRemain?:         number
      discardRemain?:      number
    }
    if (p.matchHeader) {
      // SEQ=10194 STEP=3: DAL.ReadAllAsList 배열 직렬화 대응 — 배열이면 [0] 인덱스 추출.
      const mh: Partial<TtrGame> | undefined = Array.isArray(p.matchHeader)
        ? (p.matchHeader[0] as Partial<TtrGame> | undefined)
        : (p.matchHeader as Partial<TtrGame>)
      if (mh) {
        _game = {
          matchId:             mh.matchId             ?? 0,
          roomCode:            mh.roomCode            ?? '',
          seed:                mh.seed                ?? 0,
          status:              mh.status              ?? 'MAIN_TURN',
          currentPhase:        mh.currentPhase        ?? null,
          currentTurnUserCode: mh.currentTurnUserCode ?? null,
          turnNo:              mh.turnNo              ?? 1,
          finalTurnTriggerUserCode: mh.finalTurnTriggerUserCode ?? null,
          winnerUserCode:      mh.winnerUserCode      ?? null,
          maxSeats:            mh.maxSeats            ?? (p.players?.length ?? 0),
        }
        // catch #20 fix (SEQ=10205 STEP=1, 2026-05-26): ACTION_DONE_THIS_TURN GameLoad hydrate.
        //   SEQ=10199 STEP=1 컬럼 신설 후 GameLoad matchHeader에서 누락 → 새로고침 후 _actionDoneThisTurn=false 초기값 잔재.
        const mhAny = mh as Partial<TtrGame> & { actionDoneThisTurn?: number | boolean }
        if (typeof mhAny.actionDoneThisTurn === 'number') {
          _actionDoneThisTurn = mhAny.actionDoneThisTurn === 1
        } else if (typeof mhAny.actionDoneThisTurn === 'boolean') {
          _actionDoneThisTurn = mhAny.actionDoneThisTurn
        }
      }
    }
    if (p.players)         _players          = p.players
    if (p.faceUpCards)     _faceUp           = p.faceUpCards
    // routes 키는 sp_TtrGameLoad 4번 결과셋 = TTR_ROUTE_CLAIM (점유 노선). 정적 마스터가 아니므로 _claims로 매핑. SEQ=10187 hot fix.
    if (p.routes)          _claims           = p.routes
    if (p.routeClaims)     _claims           = p.routeClaims
    if (p.myHand) {
      // SEQ=10194 STEP=3: DAL.ReadAllAsList 배열 직렬화 대응 — [{color,count},...] → Record<string,number>.
      if (Array.isArray(p.myHand)) {
        const summary: Record<string, number> = {}
        ;(p.myHand as Array<{ color: string; count: number }>).forEach(h => {
          if (h && typeof h.color === 'string' && typeof h.count === 'number') {
            summary[h.color] = h.count
          }
        })
        _myHandSummary = summary
      } else {
        _myHandSummary = p.myHand as TtrMyHandSummary
      }
    }
    if (p.myTickets) {
      const all = p.myTickets as Array<TtrMyTicket>
      // SEQ=10194 STEP=2: 서버 6번째 결과셋(myTickets)이 ZONE IN ('HAND','PENDING_KEEP') 통합 묶음.
      // DAL이 myPendingTickets 키를 분리 직렬화하지 않으므로 클라에서 ZONE 분기로 _myTickets·_myPendingTickets 분배.
      _myTickets = all.filter(t => t.zone !== 'PENDING_KEEP')
      _myPendingTickets = all
        .filter(t => t.zone === 'PENDING_KEEP')
        .map(t => ({
          ticketId: t.ticketId,
          cityAId:  t.cityAId,
          cityBId:  t.cityBId,
          points:   t.points,
          isLong:   t.isLong,
        }))
    }
    if (p.myPendingTickets) _myPendingTickets = p.myPendingTickets
    // SEQ=10198 catch #7·9 fix: sp_TtrGameLoad faceUpCards 결과셋의 per-slot DECK_REMAIN/DISCARD_REMAIN 활용
    // (top-level p.deckRemain 키 미존재 → faceUpCards[0]에서 추출)
    if (typeof p.deckRemain === 'number') {
      _deckRemain = p.deckRemain
    } else if (Array.isArray(p.faceUpCards) && p.faceUpCards.length > 0) {
      const first = p.faceUpCards[0] as { deckRemain?: number }
      if (typeof first.deckRemain === 'number') _deckRemain = first.deckRemain
    }
    if (typeof p.discardRemain === 'number') {
      _discardRemain = p.discardRemain
    } else if (Array.isArray(p.faceUpCards) && p.faceUpCards.length > 0) {
      const first = p.faceUpCards[0] as { discardRemain?: number }
      if (typeof first.discardRemain === 'number') _discardRemain = first.discardRemain
    }
    // SEQ=10194 STEP=3: DAL.ReadAllAsList 배열 직렬화 대응 — [{ticketDeckRemain,ticketDiscarded}] → number.
    if (Array.isArray(p.ticketDeckRemain)) {
      const first = (p.ticketDeckRemain as Array<{ ticketDeckRemain?: number }>)[0]
      if (first && typeof first.ticketDeckRemain === 'number') {
        _ticketDeckRemain = first.ticketDeckRemain
      }
    } else if (typeof p.ticketDeckRemain === 'number') {
      _ticketDeckRemain = p.ticketDeckRemain
    }
    if (p.cardCounts) {
      p.cardCounts.forEach(c => {
        const ex = _otherCounts[c.userCode] ?? { cardCount: 0, ticketCount: 0 }
        _otherCounts[c.userCode] = { ...ex, cardCount: c.cardCount }
      })
    }
    if (p.ticketCounts) {
      p.ticketCounts.forEach(c => {
        const ex = _otherCounts[c.userCode] ?? { cardCount: 0, ticketCount: 0 }
        _otherCounts[c.userCode] = { ...ex, ticketCount: c.ticketCount }
      })
    }
    if (p.trainRemain) {
      _players = _players.map(pl => {
        const t = p.trainRemain!.find(x => x.userCode === pl.userCode)
        return t ? { ...pl, trainRemain: t.trainRemain } : pl
      })
    }
    // 본인 BFS 라이브 갱신
    this.recalcMyTicketCompletion()
    // 본인 펜딩 티켓 있으면 AWAIT_TICKET_KEEP 복원
    // (DEAL_TICKETS SETUP 단계는 currentTurnUserCode=NULL이라 본인 턴 조건 우회 — applyTicketsDealtCaller L462 정책 정합)
    if (_myPendingTickets.length > 0 &&
        (_game?.status === 'DEAL_TICKETS' || _myUserCode === _game?.currentTurnUserCode)) {
      _mySubPhase = 'AWAIT_TICKET_KEEP'
    }
    _notify()
  },

  applyGameStarted(p: TtrGameStartedPayload): void {
    _game = {
      matchId:             p.matchId,
      roomCode:            p.roomCode,
      seed:                p.seed,
      status:              'DEAL_TICKETS',
      currentPhase:        null,
      currentTurnUserCode: null,
      turnNo:              0,
      maxSeats:            p.players.length,
    }
    _players          = p.players
    _faceUp           = p.faceUpCards
    _deckRemain       = p.deckRemain
    _ticketDeckRemain = p.ticketDeckRemain
    _discardRemain    = 0
    _claims           = []
    _myHandSummary    = {}
    _myTickets        = []
    _myPendingTickets = []
    _myPendingFirstCard = null
    _mySubPhase       = 'IDLE'
    _actionDoneThisTurn = false  // SEQ=10199 STEP=1
    _otherCounts      = {}
    _gameEnded        = null
    _notify()
  },

  applyTicketsDealtCaller(p: TtrTicketsDealtCallerPayload): void {
    _myPendingTickets = p.tickets
    // SETUP 단계 keep 모달 — 본인 턴 여부와 무관하게 전원 동시 keep
    _mySubPhase       = 'AWAIT_TICKET_KEEP'
    _notify()
  },

  applyTicketsDealt(p: TtrTicketsDealtPayload): void {
    // 타인 카운트 갱신 (본인 호출은 applyTicketsDealtCaller 가 별도)
    const existing = _otherCounts[p.userCode] ?? { cardCount: 0, ticketCount: 0 }
    _otherCounts[p.userCode] = { ...existing, ticketCount: p.ticketCount }
    _notify()
  },

  applyTurnStarted(p: TtrTurnStartedPayload): void {
    if (_game) {
      _game = {
        ...(_game),
        currentTurnUserCode: p.currentTurnUserCode,
        turnNo:              p.turnNo,
        currentPhase:        p.currentPhase,
        status:              _game.status === 'DEAL_TICKETS' ? 'MAIN_TURN' : _game.status,
      }
      // 새 턴 시작 시 서브 상태 IDLE 복귀 (본인 펜딩이 남아 있으면 응답 자동 다이얼로그)
      _mySubPhase         = 'IDLE'
      _myPendingFirstCard = null
      // SEQ=10199 STEP=1: 새 턴 시작 — 액션 가드 리셋 (다음 USER도 본인 클라 입장에서 0건 시작)
      _actionDoneThisTurn = false
      _notify()
    }
  },

  applyCardDrawn(p: TtrCardDrawnPayload): void {
    _faceUp        = p.newFaceUpCards
    _deckRemain    = p.deckRemain
    _discardRemain = p.discardRemain
    const existing = _otherCounts[p.userCode] ?? { cardCount: 0, ticketCount: 0 }
    _otherCounts[p.userCode] = { ...existing, cardCount: existing.cardCount + 1 }
    // SEQ=10199 STEP=1: 본인 카드 드로우 broadcast 시 액션 가드 SET true.
    // (caller 전용 broadcast applyCardDrawnCaller도 동일 처리하지만, broadcast 우선 보장)
    if (_myUserCode !== null && p.userCode === _myUserCode) {
      _actionDoneThisTurn = true
    }
    _notify()
  },

  applyCardDrawnCaller(p: TtrCardDrawnCallerPayload): void {
    _myHandSummary[p.cardColor] = (_myHandSummary[p.cardColor] ?? 0) + 1
    // SEQ=10199 STEP=1: 본인 카드 드로우 — 액션 가드 SET true (1장째에도, 2장째에도 idempotent).
    _actionDoneThisTurn = true
    // catch #14 잔존 회수 (SEQ=10201 STEP=4): 서버 권위 awaitSecond 직접 사용.
    //   1 = AWAIT_SECOND_CARD 진입 (비공개 덱 와일드 포함 — 룰 §2-2 정합)
    //   0 = turn end 또는 두 번째 카드 완료 (서버 AutoEndTurn 발동 또는 IDLE 복귀)
    //   기존 cardColor !== 'WILD' 추측 분기 폐지 — 비공개 덱 와일드 룰 위반 회로 차단.
    if (p.awaitSecond === 1) {
      _mySubPhase = 'AWAIT_SECOND_CARD'
      _myPendingFirstCard = { color: p.cardColor, sourceSlot: null }
    } else {
      // awaitSecond=0: turn end or 두 번째 카드 완료. 서버 broadcast (TtrTurnStarted)가 곧 덮어쓰지만 즉시 IDLE 복귀로 UX 정합.
      _mySubPhase = 'IDLE'
      _myPendingFirstCard = null
    }
    _notify()
  },

  applyRouteClaimed(p: TtrRouteClaimedPayload): void {
    // 점유 추가
    const owner = _players.find(pl => pl.userCode === p.userCode)
    _claims.push({
      routeId:       p.routeId,
      laneNo:        p.laneNo,
      ownerUserCode: p.userCode,
      color:         owner?.color ?? '',
      turnNo:        _game?.turnNo ?? 0,
    })
    // SEQ=10200 STEP=1: catch #11 시각 강화 — 최근 점유 lane 1.5초 추적 SET.
    //   이전 미해제 타이머 clearTimeout (연속 클레임 시 직전 ring 즉시 종료).
    if (_recentClaimTimer !== null) {
      clearTimeout(_recentClaimTimer)
      _recentClaimTimer = null
    }
    _recentClaim = {
      routeId:       p.routeId,
      laneNo:        p.laneNo,
      scoreDelta:    p.scoreDelta,
      ownerUserCode: p.userCode,
      ownerColor:    owner?.color ?? '',
      ts:            Date.now(),
    }
    _recentClaimTimer = setTimeout(() => {
      _recentClaim       = null
      _recentClaimTimer  = null
      _notify()
    }, 1500)
    // 본인 핸드 차감 — colorUsed (N - wildCount)장 + WILD wildCount장
    if (_myUserCode !== null && p.userCode === _myUserCode) {
      const colorCount = p.trainsConsumed - p.wildCount
      if (colorCount > 0 && p.colorUsed && p.colorUsed !== 'WILD') {
        _myHandSummary[p.colorUsed] = Math.max(0, (_myHandSummary[p.colorUsed] ?? 0) - colorCount)
      }
      if (p.wildCount > 0) {
        _myHandSummary['WILD'] = Math.max(0, (_myHandSummary['WILD'] ?? 0) - p.wildCount)
      }
      // SEQ=10199 STEP=1: 본인 노선 클레임 — 액션 가드 SET true.
      _actionDoneThisTurn = true
    }
    // 점수 / 트레인 갱신
    _players = _players.map(pl =>
      pl.userCode === p.userCode
        ? { ...pl, score: p.newScore, trainRemain: pl.trainRemain - p.trainsConsumed }
        : pl)
    // catch #21 fix (SEQ=10205 STEP=1): 사용 카드 → DISCARD 이동 후 클라 _discardRemain 갱신.
    //   DRAW=0 + DISCARD>0 상태에서도 button '재셔플 가능' 활성화 트리거 보장.
    if (typeof p.newDiscardRemain === 'number') {
      _discardRemain = p.newDiscardRemain
    }
    // 본인 노선 추가 → BFS isCompleteUx 라이브 갱신
    if (_myUserCode !== null && p.userCode === _myUserCode) {
      this.recalcMyTicketCompletion()
    }
    _notify()
  },

  applyScoreUpdated(p: TtrScoreUpdatedPayload): void {
    _players = _players.map(pl => pl.userCode === p.userCode ? { ...pl, score: p.newScore } : pl)
    _notify()
  },

  applyTrainRemainUpdated(p: TtrTrainRemainUpdatedPayload): void {
    _players = _players.map(pl => pl.userCode === p.userCode ? { ...pl, trainRemain: p.trainRemain } : pl)
    _notify()
  },

  applyFaceUpRefilled(p: TtrFaceUpRefilledPayload): void {
    _faceUp     = p.newFaceUpCards
    _deckRemain = p.deckRemain
    _notify()
  },

  applyTicketsDrawn(p: TtrTicketsDrawnPayload): void {
    const existing = _otherCounts[p.userCode] ?? { cardCount: 0, ticketCount: 0 }
    _otherCounts[p.userCode] = { ...existing, ticketCount: existing.ticketCount + p.ticketCount }
    _ticketDeckRemain = Math.max(0, _ticketDeckRemain - p.ticketCount)
    // SEQ=10199 STEP=1: 본인 티켓 드로우 — 액션 가드 SET true (broadcast 우선 보장).
    if (_myUserCode !== null && p.userCode === _myUserCode) {
      _actionDoneThisTurn = true
    }
    _notify()
  },

  applyTicketsDrawnCaller(p: TtrTicketsDrawnCallerPayload): void {
    _myPendingTickets = p.tickets
    _mySubPhase       = 'AWAIT_TICKET_KEEP'
    // SEQ=10199 STEP=1: 본인 티켓 드로우 caller — 액션 가드 SET true.
    _actionDoneThisTurn = true
    _notify()
  },

  /**
   * 티켓 keep broadcast (전체 — 카운트만 공개).
   * 본인 호출 시 _myPendingTickets 중 keep된 ticketId를 _myTickets로 이동하는 처리는
   * 클라가 invoke keepTickets(ids) 직후 직접 수행 (applyMyTicketsKeptLocal).
   * 본 broadcast는 서버 권위 확정 후 호출되므로 본인 _myPendingTickets에 잔여가 있으면
   * (UI 동시성 racing) 정리만 보장한다.
   */
  applyTicketsKept(p: TtrTicketsKeptPayload): void {
    if (_myUserCode !== null && p.userCode === _myUserCode) {
      // 펜딩 잔여 정리 — 클라가 이미 _myTickets로 옮겼다면 no-op
      _myPendingTickets = []
      _mySubPhase       = 'IDLE'
    }
    _notify()
  },

  /**
   * 본인 keepTickets invoke 직후 클라가 호출 — UI 즉시 반영(서버 broadcast 대기 없이).
   * keepIds 에 포함된 ticketId만 _myTickets로 이동, 나머지는 폐기.
   * 본 함수는 서버 권위 confirm 전 낙관적 반영 — TtrActionError 발생 시 GameState 재로드 필요.
   */
  applyMyTicketsKeptLocal(keepIds: number[]): void {
    const keepSet = new Set(keepIds)
    const moved: TtrMyTicket[] = _myPendingTickets
      .filter(t => keepSet.has(t.ticketId))
      .map(t => ({
        ticketId: t.ticketId,
        cityAId:  t.cityAId,
        cityBId:  t.cityBId,
        points:   t.points,
        isLong:   t.isLong,
        isCompleteUx: false,
      }))
    _myTickets        = [..._myTickets, ...moved]
    _myPendingTickets = []
    _mySubPhase       = 'IDLE'
    this.recalcMyTicketCompletion()
    _notify()
  },

  applyFinalTurnTriggered(p: TtrFinalTurnTriggeredPayload): void {
    if (_game) {
      _game = { ..._game, status: 'FINAL_TURN', finalTurnTriggerUserCode: p.triggerUserCode }
      _notify()
    }
  },

  applyGameEnded(p: TtrGameEndedPayload): void {
    _gameEnded = p
    if (_game) {
      _game = { ..._game, status: 'GAME_END', winnerUserCode: p.winnerUserCode }
    }
    // 종료 시 전원 티켓 공개 — _players 의 finalScore 갱신
    _players = _players.map(pl => {
      const sb = p.scoreBreakdown.find(s => s.userCode === pl.userCode)
      const lp = p.longestPathHolders.includes(pl.userCode)
      return sb ? { ...pl, finalScore: sb.total, score: sb.total, isLongestPath: lp } : pl
    })
    _notify()
  },

  /** 게임 종료 / 떠나기 / 로그아웃 시 전체 초기화. */
  clearGame(): void {
    _game             = null
    _players          = []
    _claims           = []
    _faceUp           = []
    _deckRemain       = 0
    _discardRemain    = 0
    _ticketDeckRemain = 0
    _myHandSummary    = {}
    _myTickets        = []
    _myPendingTickets = []
    _myPendingFirstCard = null
    _mySubPhase       = 'IDLE'
    _actionDoneThisTurn = false  // SEQ=10199 STEP=1
    _otherCounts      = {}
    _gameEnded        = null
    // SEQ=10200 STEP=1: 미해제 ring 타이머 잔재 차단.
    if (_recentClaimTimer !== null) {
      clearTimeout(_recentClaimTimer)
      _recentClaimTimer = null
    }
    _recentClaim      = null
    // _myUserCode·정적 마스터(_cities/_routes/_ticketsMaster)는 유지 (게임 무관, 캐시)
    _notify()
  },
}
